<!-- The following component should verify/handle the following logic:
	1.	Some brokers require the cargo policy to be on file.
	2.	Some brokers do not require the cargo policy upload. For these, we only need to show the “select exclusions” option.
*****************	*****************	*****************	
Notes:
Every flow should account for and override the firstTime logic depending on the Onboarding Packet information.
If the normal flow indicates it’s the user’s first time, but the Onboarding Packet shows that the user has already uploaded the cargo policy,
the firstTime logic should be bypassed. Most of the changes related to firstTime and button statuses are handled in the watcher for StepName. -->
<template>
  <div>
    <div v-if="isCargoPolicyUploadRequired">
      <UploadCargoPolicy
        v-if="
          firstTime ||
          (!isCargoPolicyUploadProvided && !latestInsuranceCargoPolicy)
        "
        :cargo-policy="latestInsuranceCargoPolicy"
        @file-upload="handleFile"
      />
      <ViewOnlyCargoPolicy
        v-else
        :cargo-policy="latestInsuranceCargoPolicy"
      />
    </div>
    <div v-else>
      <p class="mb-8 mt-4 text-left text-dark-gray sm:mt-2">
        {{
          firstTime
            ? 'Do you have any of the following policy exclusions?'
            : 'Please confirm your policy exclusions below.'
        }}
      </p>
      <div v-if="firstTime">
        <p class="mb-2 border-b-2 border-light-gray text-left text-body-color">
          Cargo Policy Exclusions
        </p>
        <div class="flex flex-wrap items-start">
          <div
            v-for="item in getAndFormatTypes(
              $store.getters['insuranceExclusions/isTypes']
            )"
            :key="item.name"
            class="w-1/2 p-1 md:w-1/3"
          >
            <radio-button
              :item="item.name"
              :active="
                data.cargo_insurance?.policy_exclusions?.includes(item.value)
              "
              :handle-select="handleSelect"
              :name="item.value"
            />
          </div>
        </div>
      </div>
      <div v-else>
        <p
          class="mb-2 text-left text-2xs font-bold uppercase text-gray-over-dark"
        >
          Cargo Policy Exclusions
        </p>

        <div
          v-if="policyExclusions?.length > 0"
          class="flex flex-wrap"
        >
          <p
            v-for="(item, index) in policyExclusions"
            :key="item"
            class="mr-1 text-dark-gray"
          >
            {{
              `${titleCase(item?.replace(/_/g, ' '))}${index !== policyExclusions?.length - 1 ? ', ' : ''}`
            }}
          </p>
        </div>
      </div>
    </div>
    <form-navigation-buttons
      v-if="!isNavigationDisabled"
      :enabled="!isNavigationDisabled"
      :component-identifier="componentIdentifier"
      :buttons-to-display="buttonsToDisplay"
      :is-button-disabled="isButtonDisabled"
      :loading="hardStopLoading"
      @next="handleNext"
      @back="handleBack"
      @edit="handleEdit"
      @exit-modal="emitExitModal"
    />
  </div>
</template>

<script>
import FormNavigationButtons from '@/components/FormNavigationButtons.vue';
import { useFormRouter } from '@/composables/formRouter';
import { useStore } from 'vuex';
import { ref, computed } from 'vue';
import RadioButton from '@/components/RadioButton.vue';
import UploadCargoPolicy from '@/components/steps/Insurance/UploadCargoPolicy.vue';
import ViewOnlyCargoPolicy from '@/components/steps/Insurance/ViewOnlyCargoPolicy.vue';
import { useToast } from 'vue-toast-notification';

export default {
  name: 'InsuranceStepThree',
  components: {
    RadioButton,
    FormNavigationButtons,
    UploadCargoPolicy,
    ViewOnlyCargoPolicy,
  },
  props: {
    data: Object,
    updateField: Function,
    nextStep: Function,
    formPosition: Number,
    title: String,
    stepName: String,
    skipStep: Function,
    screenSize: String,
  },
  setup(_, { emit }) {
    const store = useStore();
    const toast = useToast();
    const hardStopLoading = ref(undefined);
    const cargoPolicyPdf = ref(null);
    const componentIdentifier = 'Insurance-3';
    const formRouter = useFormRouter(componentIdentifier);
    const isNavigationDisabled = computed(() => formRouter.isDisabled.value);
    const isCargoPolicyUploadRequired = computed(
      () => store.state.carriers.carrierData?.is_cargo_policy_upload_required
    );
    const isCargoPolicyUploadProvided = computed(
      () => store.state.carriers.carrierData?.is_cargo_policy_upload_provided
    );

    const latestInsuranceCargoPolicy = computed(
      () => store.state.carriers.carrierData?.latest_insurance_cargo_policy
    );
    const firstTime = computed(
      () =>
        store.state.firstTime?.[
          formRouter.componentStep.value?.firstTimeIdentifier
        ]
    );
    const buttonsToDisplay = computed(() => {
      if (firstTime.value) return 'continue-save';

      return 'continue-edit';
    });
    const isButtonDisabled = false;

    const handleNext = async () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleNext' called"
      );

      if (isNavigationDisabled.value) {
        return store.commit(
          'addFormDebugMessage',
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );
      }

      // Check if cargo policy upload is required and if the PDF is available
      if (isCargoPolicyUploadRequired.value && cargoPolicyPdf.value) {
        // Attempt to upload the insurance policy, and continue only if successful
        const isPolicyUploaded = await loadInsurancePolicy();

        // If the policy upload failed, stop further execution
        if (!isPolicyUploaded) {
          console.error('Failed to upload the insurance policy');
          return;
        }

        // If the policy was uploaded successfully, call getOnboardingPacket
        getOnboardingPacket();
      }

      // If no errors occurred, proceed to the next step in the form
      formRouter.moveToNextStep();
    };

    const loadInsurancePolicy = async () => {
      try {
        // Attempt to upload the cargo policy using a store action
        const res = await store.dispatch(
          'insurance/uploadCargoPolicy',
          cargoPolicyPdf.value
        );

        // Check if there are errors in the response related to the cargo policy
        if (
          res.data.insurance_cargo_policy &&
          res.data.insurance_cargo_policy.length > 0
        ) {
          const messageFormatted =
            res.data?.insurance_cargo_policy?.[0]?.charAt(0)?.toUpperCase() +
            res.data?.insurance_cargo_policy?.[0]?.slice(1);
          // Show the first error message from the API response using a toast notification
          toast?.open({
            type: 'info',
            message: messageFormatted || res.data?.insurance_cargo_policy?.[0],
            position: 'top-right',
            duration: 10000,
          });

          // Hard Stop is going to be a value passed to FormNavigationButtons as a prop, and it will stop the loading animation. I’m using setTimeout here because, in a
          // scenario where the user tries to upload a document for the first time, we will emit false as the value. However, if the user tries again and we emit false again
          // , the parent component won’t re-render because the value remains false, and the watcher won’t be triggered. The idea is to first emit false, and then emit undefined
          // to the parent component, so that the watcher gets triggered again.
          hardStopLoading.value = false;
          setTimeout(() => {
            hardStopLoading.value = undefined;
          }, 500);
          return false;
        }

        return true;
      } catch (error) {
        toast?.open({
          message: 'An error occurred while uploading the policy.',
          type: 'error',
          position: 'top-right',
          duration: 5000,
        });
        console.error(error);
        return false; // Indicate that an error occurred
      }
    };

    const handleFile = (file) => {
      cargoPolicyPdf.value = file;

      store.commit('toggleDisabledButton', {
        bool: cargoPolicyPdf.value ? false : true,
        source: 'InsuranceStepThree #3',
      });
    };

    const getOnboardingPacket = async () => {
      await store.dispatch('onboardingPacket/getOnboardingPacketById', {
        id: store.state.onboardingPacket.onboarding_id,
      });
    };

    // backChecks: Checks to be reviewed when the user clicks the Back button (but before any store.state.formRoutes.activeStep changes)
    const backCheck1 = computed(
      () =>
        !store.state.firstTime['Insurance'] ||
        store.state.carriers.carrierData?.insurance_policies?.length > 0
    );

    const handleBack = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleBack' called"
      );
      if (isNavigationDisabled.value)
        return console.log(
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );

      if (backCheck1.value) {
        store.commit(
          'addFormDebugMessage',
          'BACK CHECK | ' +
            componentIdentifier +
            ': backCheck1 is TRUE (firstTime.Insurance is false or carrier has insurance_policies), moving to Insurance-1'
        );
        return formRouter.moveToStepByIdentifier('Insurance-1');
      } else {
        store.commit(
          'addFormDebugMessage',
          'BACK CHECK | ' +
            componentIdentifier +
            ': backCheck1 is FALSE (firstTime.Insurance is true and carrier has no insurance_policies)'
        );
      }

      formRouter.moveToPreviousStep();
    };

    const handleEdit = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleEdit' called"
      );
      if (isNavigationDisabled.value)
        return console.log(
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );
      formRouter.editActiveStep();
    };

    const emitExitModal = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'emitExitModal' called"
      );
      emit('exit-modal');
    };

    return {
      componentIdentifier,
      handleNext,
      handleBack,
      handleEdit,
      buttonsToDisplay,
      isButtonDisabled,
      isNavigationDisabled,
      firstTime,
      emitExitModal,
      isCargoPolicyUploadRequired,
      handleFile,
      isCargoPolicyUploadProvided,
      latestInsuranceCargoPolicy,
      hardStopLoading,
    };
  },
  data() {
    return {
      items: [],
      // itemHash: {
      //   alcohol: "Alcohol",
      //   electronics: "Electronics",
      //   employee_theft_or_fraud: "Employee Theft/Fraud",
      //   garments: "Garments",
      //   precious_metals: "Precious Metals",

      //   seafood: "Seafood",
      //   spoilage: "Spoilage",
      //   unattended_vehicle: "Unattended Vehicle",
      // },
    };
  },
  computed: {
    insuranceExclusionsIsTypes() {
      return this.$store.state.insuranceExclusions.isTypes;
    },
    policyExclusions() {
      if (this.latestExclusions?.length > 0) return this.latestExclusions;
      return (
        this.$store.state.carriers.carrierData?.cargo_insurance
          ?.policy_exclusions || []
      );
    },
    latestExclusions() {
      return this.$store.state.insurance.latest_exclusions;
    },
  },
  watch: {
    stepName: function (val) {
      if (val === 'Insurance-3') {
        const {
          is_cargo_policy_upload_provided,
          is_cargo_policy_upload_required,
          latest_insurance_cargo_policy,
          exclusions,
        } = this.$store.state.carriers.carrierData || {};

        // Case 1: Validate if the cargo policy is provided and the upload is required.
        // If so, we should set firstTime to false, since that means the file has already been uploaded (will need to display the confirmation screen).
        if (
          is_cargo_policy_upload_provided &&
          is_cargo_policy_upload_required
        ) {
          this.updateFirstTime(false);
          this.$store.commit('toggleDisabledButton', {
            bool: false,
            source: 'InsuranceStepThree #5',
          });
          return;
        }

        // Case 2: Validate if the cargo policy is NOT provided, the upload is required and there's some exclusions (stored exclusions marks firsTime as false).
        // the component should be reactive to bypass that. We should disable the Save & Continue, and put the component as first time because we'll need to load the
        // file.
        if (
          !is_cargo_policy_upload_provided &&
          is_cargo_policy_upload_required &&
          exclusions?.length > 0
        ) {
          this.updateFirstTime(true);
          this.$store.commit('toggleDisabledButton', {
            bool: true,
            source: 'InsuranceStepThree #4',
          });
          return;
        }

        // Case 3: If the cargo policy file is required but we don’t have the file stored yet, we should disable the “Save & Continue” button until the file
        // is uploaded (Initially, the button is always enabled because this component was designed for exclusions, and we can proceed without storing exclusions).
        if (is_cargo_policy_upload_required && !latest_insurance_cargo_policy) {
          this.$store.commit('toggleDisabledButton', {
            bool: true,
            source: 'InsuranceStepThree #1',
          });
          return;
        }

        // Force Save & Continue button to be enabled here
        this.$store.commit('toggleDisabledButton', {
          bool: false,
          source: 'InsuranceStepThree #2',
        });

        if (exclusions?.length > 0) {
          const findExclusions = exclusions.map((item) => item.is_type);
          const cargoObject = {
            ...this.data.cargo_insurance,
            policy_exclusions: findExclusions,
          };
          this.updateField('cargo_insurance', cargoObject);
          this.updateFirstTime(false);
        } else {
          this.updateFirstTime(true);
        }
      }
    },
  },
  async created() {
    await this.$store.dispatch('insuranceExclusions/getIsTypes');
  },
  methods: {
    titleCase(str) {
      const splitStr = str?.toLowerCase()?.split(' ');
      for (var i = 0; i < splitStr.length; i++) {
        splitStr[i] =
          splitStr[i]?.charAt(0)?.toUpperCase() + splitStr[i]?.substring(1);
      }
      return splitStr.join(' ');
    },
    updatePacketBody(items) {
      this.$store.commit('insurance/setLatestExclusions', items);
      this.$store.dispatch('onboardingPacket/setOnboardingBody', {
        insurance_exclusion: {
          is_types: items,
        },
      });
    },
    async updateFirstTime(firstTime) {
      await this.$store.commit('firstTime/updateStepFirstTime', {
        step: 'InsuranceExclusions',
        value: firstTime,
      });
    },
    handleSelect(e) {
      const { policy_exclusions } = this.data.cargo_insurance;
      let cargoObject = {
        ...this.data.cargo_insurance,
        policy_exclusions: [],
      };
      if (policy_exclusions && policy_exclusions.includes(e.target.name)) {
        const filteredExclusions = policy_exclusions.filter(
          (el) => el !== e.target.name
        );
        cargoObject.policy_exclusions = filteredExclusions;
      } else {
        const policyExclusions = policy_exclusions
          ? [...policy_exclusions, e.target.name]
          : [e.target.name];
        cargoObject.policy_exclusions = policyExclusions;
      }
      this.updatePacketBody(cargoObject.policy_exclusions);
      this.updateField('cargo_insurance', cargoObject);
    },
    getAndFormatTypes(types) {
      const orderedTypes = [];
      types.forEach((type) => {
        orderedTypes.push({
          name: type,
          value: this.replaceSpaces(type)?.toLowerCase(),
        });
      });
      return orderedTypes;
    },
    replaceSpaces(string) {
      if (string) {
        return string?.replace(/ /g, '_');
      }
      return string;
    },
  },
};
</script>
